import { type Conversation } from "shared/apiClient";
import { AssessmentLights } from "shared/types";
import { getText } from "shared/lib";

import { useCouncilLocalisation } from "shared/hooks";

import { ExternalLinkIcon } from "shared/components";

export interface MELTProps {
  assessmentLights: string;
  conversation: Conversation | undefined | null;
}

export function MELT({ assessmentLights, conversation }: MELTProps) {
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  const renderRequestCertificateOfTitle = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Request a Certificate of Title")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't account for covenants (restrictions), agreements, caveats, and easements which can impact what you can and can't do on a property. A Certificate of Title can include this information and can be obtained online.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href={getLocalisationEntry("councilApplyForCertificateOfTitleLink")}
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request a copy through Landata")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderCheckBuildingPermitRequirement = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Check if you need a building permit")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't account for works requiring authorisation under the Building Regulations 2018. Please visit Council's website 'do i need a permit' for further information on the types of work that need building permits. Please note Council does not issue building permits, this is arranged with private building surveyors.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.melton.vic.gov.au/Services/Building-Planning-Transport/Interactive-house-permits"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Do I need a building permit?")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderApplyForInformationOnPlanningControls = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Apply for information on planning controls")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "If you need formal written advice for your builder or surveyor that a planning permit is not required, please lodged an application for information on planning controls through Council's Community Portal. Please note you will need to log in or create an account on the portal to utilise the service.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://ecouncil.melton.vic.gov.au/eservice/communityProjectsInit.do?keyNum=6&alternateHierarchy=Y"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Community Portal")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderRefineYourProposal = () => {
    return (
      <div>
        <h3 className="text-headingmd">{getText("Refine your proposal")}</h3>

        <p className="text-bodymd mb-2">
          {getText(
            "Review your myLot report and address the missing information that will finalise the results of your advice.",
          )}
        </p>
      </div>
    );
  };

  const renderApplyForInformationOnPlanningControls_3 = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Apply for information on planning controls")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "If you need formal written advice for your builder or surveyor that a planning permit is required, please lodged an application for information on planning controls through Council's Community Portal. Please note you will need to log in or create an account on the portal to utilise the service.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://ecouncil.melton.vic.gov.au/eservice/communityProjectsInit.do?keyNum=6&alternateHierarchy=Y"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Community Portal")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderContactBuildingConciergeService = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Contact the Business Concierge Service")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "Are you setting up a home-based or small business? Your may need approvals from other areas of Council to operate the business. Please lodge an enquiry through Council's Business Concierge service and in the planning field quote your enquiry ID from myLot.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.melton.vic.gov.au/Services/Business/Business-Support/Business-Permit-Enquiry-Process"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Business Permit Checklist")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderSeekPreApplicationAdvice = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Seek pre-application advice")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "For more substantial proposals to understand the constraints and considerations and if the application warrants Council's support, it is strongly encouraged to lodge an application for pre-application advice. This allows you to arrange meetings and receive written advice from Council. Please note application documents are required.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://ecouncil.melton.vic.gov.au/eservice/communityProjectsInit.do?keyNum=6&alternateHierarchy=Y"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Community Portal")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderLearnHowToApply = () => {
    return (
      <div>
        <h3 className="text-headingmd">{getText("Learn how to apply")}</h3>

        <p className="text-bodymd mb-2">
          {getText(
            "Like more information on the planning permit applications and how to apply? Have a look at Council's website which provides a number of handy resources and explanation on the process.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.melton.vic.gov.au/Services/Building-Planning-Transport/Statutory-planning/Planning-permits/Learn-how-to-apply"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Learn how to apply")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderDiscussWithPlanners = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Discuss your proposal with a planner")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "Have questions? Contact us to speak to one of our planners. When you contact us, please tell us your report ID:",
          )}
        </p>
        <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
          {conversation?.identifier?.toUpperCase() || ""}
        </div>
      </div>
    );
  };

  switch (assessmentLights) {
    case AssessmentLights.NoPermitRequired:
      return (
        <>
          {renderRequestCertificateOfTitle()}
          {renderCheckBuildingPermitRequirement()}
          {renderApplyForInformationOnPlanningControls()}
          {renderContactBuildingConciergeService()}
          {renderDiscussWithPlanners()}
        </>
      );
    case AssessmentLights.MoreInformationRequired:
      return (
        <>
          {renderRefineYourProposal()}
          {renderSeekPreApplicationAdvice()}
          {renderRequestCertificateOfTitle()}
          {renderDiscussWithPlanners()}
        </>
      );
    case AssessmentLights.RequiresPermit:
      return (
        <>
          {renderRequestCertificateOfTitle()}
          {renderApplyForInformationOnPlanningControls_3()}
          {renderSeekPreApplicationAdvice()}
          {renderLearnHowToApply()}
          {renderDiscussWithPlanners()}
        </>
      );
    case AssessmentLights.Prohibited:
      return <>{renderDiscussWithPlanners()}</>;
  }
}
